import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Facebook, Twitter, Instagram } from 'lucide-react';
import styles from './footer.module.css';
import { DEFAULT_SERVICE_TAGS } from '../../constants/serviceTags';
import { homeGenderPath, resolveHomeGender } from '../../utils/homeGender';
import BrandLogo from '../brand/BrandLogo';

const Footer = () => {
  const location = useLocation();
  const homePath = homeGenderPath(resolveHomeGender(location.search));

  // Check if current route is dashboard
  const isDashboardRoute =
    location.pathname.startsWith('/admin') || location.pathname.startsWith('/vendor');

  // Don't render user navbar on dashboard routes
  if (isDashboardRoute) {
    return null;
  }
  return (
    <footer className={styles.footer}>
      <div className={styles.footerContainer}>
        {/* Main Footer Content */}
        <div className={styles.footerContent}>
          {/* Company Info Section */}
          <div className={styles.footerSection}>
            <div className={styles.logoSection}>
              <Link to={homePath} className={styles.logoLink} aria-label="B4BARBER home">
                <BrandLogo variant="banner" tone="onDark" />
              </Link>
              <p className={styles.companyDescription}>
                Your premier destination for professional grooming services. We provide top-quality
                haircuts, styling, and beauty treatments with a modern touch and exceptional
                customer service.
              </p>
            </div>
            <div className={styles.socialLinks}>
              <h4 className={styles.sectionTitle}>Follow Us</h4>
              <div className={styles.socialIcons}>
                <a
                  href="https://www.facebook.com/profile.php?id=61592610300483"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.socialIcon}
                  aria-label="Facebook"
                >
                  <Facebook size={20} />
                </a>
                <a
                  href="https://x.com/b4barber0"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.socialIcon}
                  aria-label="X"
                >
                  <Twitter size={20} />
                </a>
                <a
                  href="https://www.instagram.com/b4barber0"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.socialIcon}
                  aria-label="Instagram"
                >
                  <Instagram size={20} />
                </a>
              </div>
            </div>
          </div>

          {/* Quick Links Section */}
          <div className={styles.footerSection}>
            <h4 className={styles.sectionTitle}>Quick Links</h4>
            <ul className={styles.footerLinks}>
              <li>
                <Link to={homePath} className={styles.footerLink}>
                  Home
                </Link>
              </li>
              <li>
                <Link to="/services" className={styles.footerLink}>
                  Services
                </Link>
              </li>
              <li>
                <Link to="/salons" className={styles.footerLink}>
                  Find Salons
                </Link>
              </li>
              <li>
                <Link to="/about" className={styles.footerLink}>
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className={styles.footerLink}>
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/contact" className={styles.footerLink}>
                  List your salon
                </Link>
              </li>
              <li>
                <Link to="/profile" className={styles.footerLink}>
                  My Profile
                </Link>
              </li>
              <li>
                <Link to="/cart" className={styles.footerLink}>
                  Shopping Cart
                </Link>
              </li>
            </ul>
          </div>

          {/* Services Section */}
          <div className={styles.footerSection}>
            <h4 className={styles.sectionTitle}>Our Services</h4>
            <ul className={styles.footerLinks}>
              {DEFAULT_SERVICE_TAGS.map((tag) => (
                <li key={tag}>
                  <Link
                    to={`/services?tag=${encodeURIComponent(tag)}`}
                    className={styles.footerLink}
                  >
                    {tag}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info Section */}
          <div className={styles.footerSection}>
            <h4 className={styles.sectionTitle}>Contact Info</h4>
            <div className={styles.contactInfo}>
              <div className={styles.contactItem}>
                <MapPin className={styles.contactIcon} />
                <span>
                  Noida , Sector 63
                  <br />
                  Uttar Pradesh, India 201301
                </span>
              </div>
              <div className={styles.contactItem}>
                <Phone className={styles.contactIcon} />
                <a href="tel:+918540843859">+91 8540843859</a>
              </div>
              <div className={styles.contactItem}>
                <Mail className={styles.contactIcon} />
                <a href="mailto:info@timetofuture.com">info@timetofuture.com</a>
              </div>
              <div className={styles.contactItem}>
                <Clock className={styles.contactIcon} />
                <span>
                  Mon - Sat: 9:00 AM - 9:00 PM
                  <br />
                  Sunday: 10:00 AM - 7:00 PM
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className={styles.footerBottom}>
          <div className={styles.footerBottomContent}>
            <p className={styles.copyright}>© 2026 B4Barber. All rights reserved.</p>
            <div className={styles.legalLinks}>
              <Link to="/privacy" className={styles.legalLink}>
                Privacy Policy
              </Link>
              <Link to="/terms" className={styles.legalLink}>
                Terms of Service
              </Link>
              <Link to="/refund" className={styles.legalLink}>
                Refund Policy
              </Link>
              <Link to="/sitemap" className={styles.legalLink}>
                Sitemap
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
